import { WSLabelProps } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useClientQuery } from "../../../../query/users/queries";
import {
  avatarBankAccount,
  avatarGiftCard
} from "../../../../shared/constants/avatars";
import { MethodType } from "../ManageMethods/types";
import {
  getAccountLabelProps,
  getCreditCardLabelProps,
  getInternalAccountLabelProps,
  getPaymentCardLabelProps
} from "../ManageMethods/utils";
import {
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesPaymentCards
} from "@query";

/**
 * What a method id resolves to when the instrument behind it is gone — a payout
 * destination that outlived the account or card it pointed at. It is only the
 * right answer once the read for that id has *settled* with nothing: returning
 * it while the read is in flight labels every live method "Historical" for the
 * first second of a cold load.
 */
const emptyLabel = {
  text: "Historical Payout Method",
  avatar: avatarBankAccount
};

export function useMethodLabelProps(
  id?: string,
  type?: MethodType,
  options?: {
    showBalance?: boolean;
  }
) {
  const history = useHistory();
  const userId = useUserId();

  const queryExternalBankAccount =
    queriesExternalBankAccounts.getExternalBankAccount.useQuery(
      { bankAccountId: id as string },
      {
        enabled: !!id && type === "Account"
      }
    );
  const queryInternalAccount =
    queriesInternalAccounts.getInternalAccount.useQuery(
      { internalAccountId: id as string },
      {
        enabled: !!id && type === "InternalAccount"
      }
    );
  const queryInternalAccountBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: id as string },
      {
        enabled: !!id && type === "InternalAccount" && options?.showBalance
      }
    );
  const queryClient = useClientQuery(userId, {
    enabled: !!id && type === "CreditCard"
  });
  const queryPaymentCard = queriesPaymentCards.getPaymentCard.useQuery(
    { paymentCardId: id as string },
    {
      enabled: !!id && type === "PaymentCard"
    }
  );

  const isLoading = useMemo(() => {
    if (type === "Account") {
      return queryExternalBankAccount.isLoading;
    }
    if (type === "InternalAccount") {
      return queryInternalAccount.isLoading;
    }
    if (type === "CreditCard") {
      return queryClient.isLoading;
    }
    if (type === "PaymentCard") {
      return queryPaymentCard.isLoading;
    }

    return undefined;
  }, [
    type,
    queryExternalBankAccount.isLoading,
    queryInternalAccount.isLoading,
    queryClient.isLoading,
    queryPaymentCard.isLoading
  ]);

  const label = useMemo((): WSLabelProps | undefined => {
    if (type === "GiftCard") {
      return { text: "Gift card", avatar: avatarGiftCard };
    }

    if (type === "Account") {
      if (queryExternalBankAccount.data) {
        return getAccountLabelProps(queryExternalBankAccount.data);
      }

      return queryExternalBankAccount.isLoading ? undefined : emptyLabel;
    }

    if (type === "InternalAccount") {
      if (queryInternalAccount.data) {
        return getInternalAccountLabelProps(
          queryInternalAccount.data,
          history.push,
          options?.showBalance && queryInternalAccountBalance.data
            ? Number(
                queryInternalAccountBalance.data.available ??
                  queryInternalAccountBalance.data.balance
              )
            : undefined
        );
      }

      return queryInternalAccount.isLoading ? undefined : emptyLabel;
    }

    if (type === "CreditCard") {
      const creditCard = queryClient.data?.profile.savedPaymentMethods?.find(
        creditCard => creditCard.paymentMethodId === id
      );

      if (creditCard) {
        return getCreditCardLabelProps(creditCard);
      }

      return queryClient.isLoading ? undefined : emptyLabel;
    }

    if (type === "PaymentCard") {
      if (queryPaymentCard.data) {
        return getPaymentCardLabelProps(queryPaymentCard.data);
      }

      return queryPaymentCard.isLoading ? undefined : emptyLabel;
    }

    if (id || type === "Historical") {
      return emptyLabel;
    }
  }, [
    history.push,
    id,
    options?.showBalance,
    queryExternalBankAccount.data,
    queryExternalBankAccount.isLoading,
    queryClient.data?.profile.savedPaymentMethods,
    queryClient.isLoading,
    queryInternalAccount.data,
    queryInternalAccount.isLoading,
    queryInternalAccountBalance.data,
    queryPaymentCard.data,
    queryPaymentCard.isLoading,
    type
  ]);

  return { isLoading, label };
}
